import React, { useContext, useEffect, useState } from "react"
import { Div, Text } from "atomize"
import SecondaryButton from "../buttons/SecondaryButton"
import TextInput from "../inputs/TextInput"
import { theme } from "../settings/config"
import { notebookContext } from "../providers/notebook.provider"
import NotebookEntryCard from "./NotebookEntryCard"
import { LevelMark, levelPill } from "./NotebookVariants"
import {
	NOTEBOOK_ALL,
	NOTEBOOK_EXERCISE,
	entryAt,
	exerciseFitsPhase,
	filterNotebook,
	levelLabel,
	notebookLevelFilters,
	phaseLabel,
	searchNotebook,
} from "../utils/notebook.utils"

// Prendere qualcosa dal quaderno, dentro il foglio del programma: una
// lezione intera, o un esercizio per una fase — quelli di quella fase e
// quelli buoni per tutte. Toccarne uno lo prende; "Indietro" torna al
// programma senza cambiare niente.
//
// Ci sono anche quelli che i colleghi condividono nel circolo. Si parte dal
// livello della lezione (`level`), se qualcosa ce l'ha: le pastiglie dei
// livelli restringono, e ogni scheda ha già scelta la variante di quel
// livello.
function NotebookPicker({ kind, phaseID, level: lessonLevel = null, caption, onPick, onBack }) {
	const { entries, isLoading, hasFailed, loadShared, schoolID } = useContext(notebookContext)
	const [search, setSearch] = useState("")
	const [level, setLevel] = useState(undefined)

	useEffect(() => loadShared?.(), [loadShared])

	const isExercise = kind === NOTEBOOK_EXERCISE
	const fitting = (entries ?? []).filter(entry => !isExercise || exerciseFitsPhase(entry, phaseID))
	const levels = notebookLevelFilters(fitting, kind)
	// finché non se ne tocca una, la pastiglia è quella della lezione, se c'è
	const activeLevel =
		level === undefined
			? levels.some(option => option.id === lessonLevel)
				? lessonLevel
				: null
			: levels.some(option => option.id === level)
				? level
				: null
	const found = filterNotebook(searchNotebook(fitting, kind, search), NOTEBOOK_ALL, activeLevel)
	const title = isExercise
		? `Un esercizio per: ${phaseLabel(phaseID)}`
		: "Una lezione dal quaderno"

	const emptyText = isLoading
		? "Sto aprendo il quaderno..."
		: hasFailed
			? "Non riesco ad aprire il quaderno. Riprova tra un po'."
			: search.trim() !== ""
			? "Niente nel quaderno con questa ricerca"
			: isExercise
				? "Nel quaderno non ci sono ancora esercizi per questa fase. Scrivine uno, e salvalo dal segnalibro accanto."
				: "Nel quaderno non ci sono ancora lezioni. Scrivi il programma, e salvalo con «Salva nel quaderno»."

	return (
		<Div>
			<Text textSize="title" textWeight="bold" textColor={theme.colors.gunmetal}>
				{title}
			</Text>
			{(caption || activeLevel) && (
				<Text textColor={theme.colors.text_light} m={{ b: "0.25rem" }}>
					{[
						caption,
						activeLevel && activeLevel === lessonLevel
							? `Si parte dalle varianti ${levelLabel(lessonLevel)}, il livello della lezione.`
							: null,
					]
						.filter(Boolean)
						.join(" ")}
				</Text>
			)}
			<Div m={{ t: "0.5rem" }}>
				<TextInput
					value={search}
					handleChange={e => setSearch(e.target.value)}
					placeholder="Cerca nel quaderno"
					aria-label="Cerca nel quaderno"
				/>
			</Div>
			{levels.length > 0 && (
				<div
					role="group"
					aria-label="Livello"
					style={{
						display: "flex",
						gap: "0.5rem",
						margin: "0 -1rem 0.75rem",
						padding: "0 1rem",
						overflowX: "auto",
						scrollbarWidth: "none",
					}}>
					<button
						type="button"
						aria-pressed={!activeLevel}
						onClick={() => setLevel(null)}
						style={levelPill(!activeLevel, theme.colors.surface)}>
						Tutte
					</button>
					{levels.map(option => {
						const isSelected = option.id === activeLevel
						return (
							<button
								key={option.id}
								type="button"
								aria-pressed={isSelected}
								onClick={() => setLevel(isSelected ? null : option.id)}
								style={levelPill(isSelected, theme.colors.surface)}>
								<LevelMark levelID={option.id} />
							</button>
						)
					})}
				</div>
			)}
			<Div
				maxH="52vh"
				d="flex"
				flexDir="column"
				style={{ gap: "0.5rem", overflowY: "auto", overflowX: "hidden" }}>
				{found.length > 0 ? (
					found.map(entry => (
						<NotebookEntryCard
							key={entry.entryID}
							entry={entry}
							schoolID={schoolID}
							onClick={() => onPick(entryAt(entry, entry.variantID))}
							onPickVariant={variantID => onPick(entryAt(entry, variantID))}
						/>
					))
				) : (
					<Text textAlign="center" textColor={theme.colors.text_light} p="1.5rem 1rem">
						{emptyText}
					</Text>
				)}
			</Div>
			<Div m={{ t: "1rem" }}>
				<SecondaryButton
					m="auto"
					textColor={theme.colors.gunmetal}
					textSize="subheader"
					text="Indietro"
					action={onBack}
				/>
			</Div>
		</Div>
	)
}

export default NotebookPicker
